import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion(Box);

// Paleta del proyecto
const colors = {
  primary: "#0D5DB5",
  primaryLight: "#0EA5E9",
  primaryDark: "#2D4A77",
  textPrimary: "#1A2B4A",
  textSecondary: "#374151",
  textMuted: "#6B7280",
  background: "#FFFFFF",
  surface: "#F4F7FB",
};

const faqs = [
  {
    q: "¿Cuánto toma implementar el sistema?",
    a: "Menos de 24 horas. Nuestro equipo deja todo configurado para que factures desde el primer día.",
  },
  {
    q: "¿Qué pasa si me quedo sin internet?",
    a: "Tus datos están seguros en la nube y se sincronizan automáticamente al recuperar la conexión.",
  },
  {
    q: "¿El soporte técnico tiene costo adicional?",
    a: "No. Todos los planes incluyen soporte continuo, sin cargos extra.",
  },
  {
    q: "¿Puedo migrar mi inventario desde Excel?",
    a: "Sí, con importación masiva para una transición rápida y sin pérdida de datos.",
  },
  {
    q: "¿Cumple con los requisitos del SRI?",
    a: "Sí. Cada comprobante sigue el esquema XML autorizado y se firma y envía con un clic.",
  },
];

function FaqItem({ item, index, isOpen, onToggle }) {
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      sx={{
        borderRadius: "12px",
        bgcolor: colors.background,
        overflow: "hidden",
        boxShadow: isOpen
          ? "0 12px 30px rgba(0,0,0,0.15)"
          : "0 4px 12px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
        transform: isOpen ? "scale(1.01)" : "scale(1)",
        mb: 2,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          bgcolor: isOpen ? colors.primaryLight : "transparent",
          transition: "background-color 0.3s ease",
        },
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 2.2,
          px: 3,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: isOpen ? colors.primary : colors.textPrimary,
            lineHeight: 1.4,
            flex: 1,
            transition: "color 0.2s",
          }}
        >
          {item.q}
        </Typography>

        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: isOpen ? `${colors.primary}15` : colors.surface,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background 0.2s",
          }}
        >
          <motion.div
            animate={{ rotate: isOpen ? 135 : 0 }}
            transition={{ duration: 0.3, ease: "backOut" }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 2v10M2 7h10"
                stroke={isOpen ? colors.primary : colors.textMuted}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </motion.div>
        </Box>
      </Box>

      <AnimatePresence initial={false}>
        {isOpen && (
          <MotionBox
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            sx={{ overflow: "hidden" }}
          >
            <Box sx={{ px: 3, pb: 3, pt: 0, pr: { sm: 8 } }}>
              <Typography
                sx={{
                  color: colors.textSecondary,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                }}
              >
                {item.a}
              </Typography>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </MotionBox>
  );
}

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);
  const toggle = (i) => setOpenIndex(openIndex === i ? -1 : i);

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        bgcolor: colors.primaryDark,
        position: "relative",
        overflow: "hidden",
        py: { xs: 8, md: 12 },
        px: { xs: 3, sm: 5, md: 8 },
        boxSizing: "border-box",
      }}
    >
      {/* Fondo Geométrico Corporativo (Evitando estilo IA) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        {/* Anillos concéntricos técnicos */}
        <Box
          sx={{
            position: "absolute",
            top: -150,
            left: -100,
            width: 500,
            height: 500,
            borderRadius: "50%",
            border: `1px solid ${colors.primary}40`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -100,
            left: -50,
            width: 400,
            height: 400,
            borderRadius: "50%",
            border: `1px solid ${colors.primary}60`,
          }}
        />

        {/* Banda diagonal sólida */}
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            right: -100,
            width: "60%",
            height: 400,
            bgcolor: colors.primary,
            opacity: 0.15,
            transform: "rotate(-15deg)",
          }}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "800px", // Contenedor central más estrecho
          mx: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Encabezado Centrado */}
        <MotionBox
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ textAlign: "center", mb: 6 }}
        >
          <Typography
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "2.2rem", md: "3rem" },
              color: colors.background, // Blanco sobre fondo oscuro
              lineHeight: 1.15,
              mb: 2,
            }}
          >
            Resolvemos tus dudas
          </Typography>

          <Typography
            sx={{
              color: "#9CA3AF", // Gris claro para contraste
              fontSize: "1.1rem",
              lineHeight: 1.6,
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            Transparencia total desde el primer momento. Todo lo que necesitas
            saber para dar el paso con seguridad.
          </Typography>
        </MotionBox>

        {/* Lista de Acordeón */}
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
          {faqs.map((item, i) => (
            <FaqItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => toggle(i)}
            />
          ))}
        </Box>

        {/* Cierre de sección / Contacto alternativo */}
        <MotionBox
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
          sx={{ textAlign: "center", mt: 5 }}
        >
          <Typography sx={{ color: "#9CA3AF", fontSize: "0.95rem" }}>
            ¿Tienes un requerimiento especial? Escríbenos a{" "}
            <Box
              component="a"
              href="mailto:soporte@factusysdr.com"
              sx={{
                color: colors.primaryLight,
                textDecoration: "none",
                fontWeight: 600,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              soporte@factusysdr.com
            </Box>
          </Typography>
        </MotionBox>
      </Box>
    </Box>
  );
}
